import { useMemo, useState } from 'react'
import {
  type SortingState,
  createColumnHelper,
  flexRender,
  useTable
} from '@tanstack/react-table'
import type { Contract, Contracts } from 'prove-common'
import {
  ArrowRightIcon,
  getContractStatus,
  Mtds,
  sortableTableFeatures,
  toLocaleDateString,
  useGetLabByIdQuery
} from 'prove-common'
import { Link } from 'react-router'

type ContractsTableProps = {
  contracts: Contracts
}

function Laboratorium({ labId }: { labId: number | undefined }) {
  const { data, isFetching } = useGetLabByIdQuery(labId)
  return isFetching ? <Mtds.Skeleton /> : <>{data?.name}</>
}

const columnHelper = createColumnHelper<
  typeof sortableTableFeatures,
  Contract
>()

const columns = columnHelper.columns([
  // 1. Contract title — primary identifier
  columnHelper.accessor('title', {
    header: 'Tittel',
    cell: (info) => (
      <Link to={`/contracts/${info.row.original.id}`}>{info.getValue()}</Link>
    )
  }),
  // 2. Derived active/upcoming/expired status
  columnHelper.display({
    id: 'status',
    header: 'Status',
    cell: (info) => {
      const { startDate, endDate } = info.row.original
      const { label, color } = getContractStatus({ startDate, endDate })
      return <Mtds.Tag data-color={color}>{label}</Mtds.Tag>
    }
  }),
  // 3. Validity period
  columnHelper.display({
    id: 'periode',
    header: 'Periode',
    cell: (info) => {
      const { startDate, endDate } = info.row.original
      const from = toLocaleDateString(startDate)
      const to = toLocaleDateString(endDate)
      if (!from && !to) return '-'
      return `${from ?? '?'} – ${to ?? '?'}`
    }
  }),
  // 4. Lab
  columnHelper.accessor('labId', {
    header: 'Laboratorium',
    cell: (info) => <Laboratorium labId={info.getValue()} />
  }),
  // 5. Available analysis type count
  columnHelper.display({
    id: 'analysetyper',
    header: 'Tilgjengelige analyser',
    enableSorting: false,
    cell: (info) => {
      const types = info.row.original.analysisTypes ?? []
      const total = types.length
      if (total === 0) return <Mtds.Muted>Ingen</Mtds.Muted>
      return <span>{total} totalt</span>
    }
  }),
  // 6. Navigation arrow
  columnHelper.display({
    id: 'actions',
    cell: () => (
      <Mtds.Flex data-justify='end'>
        <ArrowRightIcon data-size='lg' />
      </Mtds.Flex>
    )
  })
])

export default function ContractsTable({
  contracts
}: Readonly<ContractsTableProps>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const sortedContracts = useMemo(
    () =>
      [...contracts].sort((a, b) => {
        const statusOrder = { Aktiv: 0, Kommende: 1, Utløpt: 2 }
        const aStatus = getContractStatus(a).label as keyof typeof statusOrder
        const bStatus = getContractStatus(b).label as keyof typeof statusOrder
        return (statusOrder[aStatus] ?? 3) - (statusOrder[bStatus] ?? 3)
      }),
    [contracts]
  )
  const table = useTable({
    features: sortableTableFeatures,
    data: sortedContracts,
    columns,
    state: { sorting },
    onSortingChange: setSorting
  })

  return (
    <figure className='overflow-x-auto'>
      <Mtds.Table data-align='center' aria-label='Kontrakter' data-nowrap>
        <thead>
          {table.getHeaderGroups().map(({ id, headers }) => (
            <tr key={id}>
              {headers.map((header) => (
                <Mtds.Table.ThSortable
                  id={header.id}
                  key={header.id}
                  aria-sort={header.column.getIsSorted()}
                  onClick={
                    header.column.getCanSort()
                      ? header.column.getToggleSortingHandler()
                      : undefined
                  }
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </Mtds.Table.ThSortable>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className='hover:bg-surface-hover cursor-pointer'
              onClick={(e) => e.currentTarget.querySelector('a')?.click()}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Mtds.Table>
    </figure>
  )
}
